import type { SurfaceKey } from "types/interfaces/access";
import { ACTIONS, can, canInClientView, openAction } from "utils/access";
import type { RoleProfile } from "utils/roles";
import { integrationsHomePath } from "utils/roles";

export type NavKey =
  | "home"
  | "dataStorage"
  | "integrations"
  | "ai"
  | "partnerDirectory"
  | "secureExchange";

export type ManagementKey =
  | "userManagement"
  | "sftpManagement"
  | "partnerManagement"
  | "integrationsPlatform"
  | "botsManagement";

export interface NavItem<K> {
  key: K;
  label: string;
  path: string;
}

interface Entry<K> {
  key: K;
  label: string;
  /** A function when the destination depends on the user. */
  path: string | ((profile: RoleProfile) => string);
  action: string;
}

const surface = (s: SurfaceKey) => openAction(s);

const SIDEBAR: Entry<Exclude<NavKey, "home">>[] = [
  {
    key: "dataStorage",
    label: "Data Storage",
    path: "/data-storage",
    action: surface("data_storage"),
  },
  {
    key: "integrations",
    label: "Integrations",
    // A client portal user's Integrations is the Integration Platform.
    path: integrationsHomePath,
    action: surface("integrations"),
  },
  {
    key: "ai",
    label: "AI Reporting",
    path: "/chat",
    action: ACTIONS.AI_AGENT_OPEN,
  },
  {
    key: "partnerDirectory",
    label: "Partner Directory",
    path: "/partners",
    action: surface("partner_directory"),
  },
  {
    key: "secureExchange",
    label: "Secure Exchange",
    path: "/secure-exchange",
    action: surface("secure_exchange"),
  },
];

const MANAGEMENT: Entry<ManagementKey>[] = [
  {
    key: "userManagement",
    label: "User Management",
    path: "/user-management",
    action: surface("user_management"),
  },
  {
    key: "sftpManagement",
    label: "SFTP Management",
    path: "/sftp-management",
    action: surface("sftp_management"),
  },
  {
    key: "partnerManagement",
    label: "Partner Management",
    path: "/partner-management",
    action: surface("partner_management"),
  },
  {
    key: "integrationsPlatform",
    label: "Integrations Platform",
    path: "/integrations-beta",
    action: surface("integrations_platform"),
  },
  {
    key: "botsManagement",
    label: "Bots Management",
    path: "/bots-management",
    action: surface("bots_management"),
  },
];

function join(basePath: string, path: string): string {
  if (!path || path === "/") return basePath || "/";
  return `${basePath}${path}`;
}

/** In Client View pass the viewer's own profile as `realProfile`. */
function visible(
  profile: RoleProfile,
  action: string,
  realProfile?: RoleProfile,
): boolean {
  return realProfile === undefined
    ? can(profile, action)
    : canInClientView(profile, realProfile, action);
}

export function getSidebarNav(
  profile: RoleProfile,
  basePath = "",
  realProfile?: RoleProfile,
): NavItem<NavKey>[] {
  return [
    { key: "home" as const, label: "Home", path: join(basePath, "/") },
    ...SIDEBAR.filter((e) => visible(profile, e.action, realProfile)).map(
      ({ key, label, path }) => ({
        key,
        label,
        path: join(basePath, typeof path === "function" ? path(profile) : path),
      }),
    ),
  ];
}

export function getManagementMenu(
  profile: RoleProfile,
  basePath = "",
  realProfile?: RoleProfile,
): NavItem<ManagementKey>[] {
  return MANAGEMENT.filter((e) => visible(profile, e.action, realProfile)).map(
    ({ key, label, path }) => ({
      key,
      label,
      path: join(basePath, typeof path === "function" ? path(profile) : path),
    }),
  );
}

/**
 * Active-state test for a nav entry: the exact path or a descendant of it,
 * never a sibling that merely shares a prefix ("/integrations" must not light
 * up on "/integrations-beta"). Callers special-case their home entry, which
 * is a prefix of everything.
 */
export function isNavPathActive(pathname: string, path: string): boolean {
  const strip = (value: string) =>
    value.length > 1 ? value.replace(/\/+$/, "") : value;
  const current = strip(pathname);
  const target = strip(path);
  if (!target || target === "/") return current === "" || current === "/";
  return current === target || current.startsWith(`${target}/`);
}
